import app from "./slices/app";
import user from "./slices/user";
import pickup from "./slices/pickup";
import inmutable from "./slices/inmutable";
import storage from "redux-persist/lib/storage";
import shipmentCreate from "./slices/shipmentCreate";
import priceCalculator from "./slices/priceCalculator";
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  FLUSH,
  PAUSE,
  PURGE,
  PERSIST,
  REGISTER,
  REHYDRATE,
  persistStore,
  persistReducer,
  PersistedState,
} from "redux-persist";
import dispatch from "./slices/dispatch";
import receipt from "./slices/receipt";
import inventory from "./slices/inventory";

// Combine the reducers into a single root reducer
const reducer = combineReducers({
  app,
  user,
  pickup,
  inmutable,
  shipmentCreate,
  priceCalculator,
  dispatch,
  receipt,
  inventory,
});

// Create the configuration object for redux-persist
const persistConfig = {
  storage,
  // Change this number if you want to discard the persisted state (e.g. after a new redux state object is added  )
  // 20 → 21: no hay `whitelist`, así que `pickup.owner` y `shipmentCreate.owner`
  // viven en localStorage y sobreviven al despliegue. Sin este bump, la guarda
  // nueva validaría un titular rehidratado de antes del fix y facturaría a la
  // cuenta equivocada sin que nada aguas abajo lo señale.
  //
  // El 20 ya se gastó en master (release 2026-09-16, forma nueva de
  // shipmentCreate), así que esta rama tuvo que subir a 21: un bump al mismo
  // número no invalida nada para quien ya cargó producción.
  version: 21,
  key: "root",
  migrate: (state: PersistedState) => {
    if (state && state._persist.version !== persistConfig.version) {
      // Targeted, not scorched earth. Only the shape of shipmentCreate changed:
      // its shipment list used to be derived from the document and now is fed
      // explicitly, so a list persisted by the previous version may hold light
      // rows with no items, which the payment screen iterates. Everything else
      // a cashier had in flight survives the upgrade.
      //
      // El titular sí se descarta, en las dos slices: la guarda de dirección
      // fiscal valida el objeto que rehidrata, y uno guardado antes del fix
      // pasa la validación con datos que hoy ya no se aceptan. Desde que este
      // migrate es dirigido, subir `version` por sí solo no lo borraba.
      const previous = state as PersistedState & {
        shipmentCreate?: Record<string, unknown>;
        pickup?: Record<string, unknown>;
      };
      return Promise.resolve({
        ...previous,
        shipmentCreate: {
          ...previous.shipmentCreate,
          shipments: [],
          document: undefined,
          owner: undefined,
        },
        pickup: {
          ...previous.pickup,
          owner: undefined,
        },
      } as PersistedState);
    }
    return Promise.resolve(state);
  },
};
const persistedReducer = persistReducer(persistConfig, reducer);

// Create the Redux store using configureStore and the persisted reducer
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

// Infer the RootState and AppDispatch types from the store itself
export const persistor = persistStore(store);
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppInventory= typeof store.dispatch;